/**
 * The searchable ServiceTitan job list behind manual job attribution
 * (ENG-2440). Presentational and fully controlled, so the search state machine
 * and its transport can be exercised without a dialog — see
 * `manual-job-match-form.tsx` for the stateful half.
 *
 * The job *number* is the identifier on screen throughout; the internal
 * ServiceTitan job id only ever travels in the request payload.
 */
import { CheckIcon } from "@heroicons/react/24/outline";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { Stack } from "@/core/components/ui/stack";
import type { ApJobSearchResult } from "@/accounts-payable/types";
import {
  jobSearchMeta,
  jobSearchStatusMessage,
} from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";

export const JobSearchCombobox = ({
  query,
  onQueryChange,
  jobs,
  isLoading,
  isFetching,
  isError,
  isRateLimited,
  selectedJob,
  onSelect,
  disabled,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  jobs: ApJobSearchResult[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  /** ServiceTitan throttled the search — reported apart from a generic failure. */
  isRateLimited?: boolean;
  selectedJob: ApJobSearchResult | null;
  onSelect: (job: ApJobSearchResult) => void;
  disabled?: boolean;
}) => {
  const statusMessage = jobSearchStatusMessage({
    query,
    isLoading,
    isError,
    isRateLimited,
    resultCount: jobs.length,
  });

  return (
    <Stack gap="xs" fullWidth>
      <Command
        shouldFilter={false}
        label="Search ServiceTitan jobs"
        className="overflow-visible rounded-lg border border-stroke-tertiary"
      >
        <CommandInput
          value={query}
          onValueChange={onQueryChange}
          disabled={disabled}
          placeholder="Search by job number, customer, or address"
        />
        <CommandList className="max-h-64 overflow-y-auto">
          {statusMessage ? (
            <Stack
              align="center"
              fullWidth
              role="status"
              className="px-4 py-6 text-center text-md text-tertiary-text"
            >
              {statusMessage}
            </Stack>
          ) : (
            <CommandGroup>
              {jobs.map((job) => {
                const isSelected = selectedJob?.id === job.id;
                return (
                  <CommandItem
                    key={job.id}
                    value={String(job.id)}
                    // Not just the input: an option still fires onSelect while
                    // the match request is in flight, which would move the
                    // checkmark and the confirm label off the job actually
                    // being submitted.
                    disabled={disabled}
                    onSelect={() => onSelect(job)}
                    className={cn("items-start gap-3 px-3 py-2", {
                      "bg-bg-secondary": isSelected,
                    })}
                  >
                    <CheckIcon
                      className={cn("mt-1 h-4 w-4 flex-none", {
                        "opacity-0": !isSelected,
                      })}
                    />
                    <Stack gap="none" className="min-w-0 flex-1">
                      <Stack
                        direction="row"
                        gap="sm"
                        align="baseline"
                        className="flex-wrap"
                      >
                        <span className="font-semibold">
                          Job {job.job_number}
                        </span>
                        <span className="truncate text-md text-secondary-text">
                          {job.customer_name}
                        </span>
                      </Stack>
                      {job.address && (
                        <span className="truncate text-sm text-tertiary-text">
                          {job.address}
                        </span>
                      )}
                      <span className="text-xs text-quaternary-text">
                        {jobSearchMeta(job).join(" · ")}
                      </span>
                    </Stack>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
      {isFetching && !statusMessage && (
        <span className="text-xs text-tertiary-text">Searching jobs…</span>
      )}
    </Stack>
  );
};
